import { Check } from '@sparkware/app-utils';
import { IServiceFactoryExtended } from '../../../../service-factory-extended.interface';
import { VideoLiveStreamBasePlayer } from '../video-live-stream-player-base';

const VIDEO_STREAM_MIME_TYPE = 'application/vnd.apple.mpegurl';

/**
 * Intervals used in this class are for fixing #1767947 and #1773060
 * The normal flow is like this:
 *      - video element triggers a 'playing' or a 'canplay' event. On this event 
 *        the loading state is set to false (loading screen is hidden and the watch time counter resumes)
 *      - video element triggers a 'waiting' event. On this event the loading state
 *        is set to true, so the loading screen appears and the watch time counter is paused.
 * BUT, this events are not triggered also on Android. The 'waiting' event is not triggered but the video freeze so a loading is in progress
 * causing the loading screen to not appear and the watch time to still count how much the user is watching the movie.
 * 
 * The solution for this, is to have a setInterval of 100ms and on each tick to check the playhead position against the previous playhead position.
 * If they are the same, it means that the video is loading.
 */
const TICK_INTERVAL = 100;
// const that describe on how many ticks we want to check fps
// in this case, we want to check FPS on every 1 second 
const TICK_REPEATS_FOR_FPS_CHECK = 10;
export class LiveStreamNativePlayer extends VideoLiveStreamBasePlayer {
    private _tickInterval = null;
    private _previousTime = 0;

    private _isVideoPlaybackQualityAvailable: boolean = false;
    private _lastPeriodDroppedFrames: number = 0;
    private _lastPeriodDecodedFrames: number = 0;
    private _tickCount: number = 0;

    public static isSupported(videoEl: HTMLVideoElement): boolean {
        const validCanPlayValue = ['probably', 'maybe'];
        const canPlayResultValue = videoEl.canPlayType(VIDEO_STREAM_MIME_TYPE);

        return validCanPlayValue.includes(canPlayResultValue);
    }

    constructor(services: IServiceFactoryExtended, gameType: number, videoReference: HTMLVideoElement, streamSource:string, watchTimeLimit: number) {
        super('native', gameType, videoReference, streamSource, watchTimeLimit, services);

        if (Check.isFunction(videoReference.getVideoPlaybackQuality)) {
            this._isVideoPlaybackQualityAvailable = true;
        }
    }

    play() {
        if (Check.isEmpty(this.videoReference.src) || this.videoReference.src !== this.streamSource) {
            this.videoReference.src = this.streamSource;
        }
        if (!this.watchTimeLimitReached) {
            this._setInterval();
        }
        super.play();
    }

    pause() {
        this._clearInterval();
        super.pause();
    }

    onWatchTimeLimitReached() {
        super.onWatchTimeLimitReached();
        this._clearInterval();
    }

    destroy() {
        this._clearInterval();
        this._isVideoPlaybackQualityAvailable = false;

        super.destroy();
    }

    private _setInterval() {
        if (!this._tickInterval) {
            this._tickInterval = setInterval(this._onTick, TICK_INTERVAL);
        }
    }

    private _clearInterval() {
        if (this._tickInterval) {
            clearInterval(this._tickInterval);
            this._tickInterval = null;
        }
    }

    private _onTick = () => {
        // if stream didn't buffered anything there is no point in trying to detect loading
        // because is clear that the video is loading
        if (this.videoReference.readyState === 0) {
            return;
        }

        const currentTime = this.videoReference.currentTime;
        if (currentTime !== this._previousTime) {
            // video is playing because the playhead is moving
            this._previousTime = currentTime;
            if (this.isLoading) {
                //if loading is already set to false, meaning that the video is playing
                // don't spam the onPlaying
                this._onPlaying();
            }
        } else {
            // video is stalled(loading) because the playhead is not moving
            if (!this.isLoading) {
                //if loading is already set to true, meaning that the video is loading
                // don't spam the onWaiting
                this._onWaiting();
            }
        }

        this._tickCount++;
        if (this._tickCount === TICK_REPEATS_FOR_FPS_CHECK) {
            this._tickCount = 0;
            this._checkFPSInterval();
        }
    }

    private _checkFPSInterval() {
        // https://developer.mozilla.org/en-US/docs/Web/API/VideoPlaybackQuality
        if (this.videoReference) {
            if (this._isVideoPlaybackQualityAvailable) {
                const videoPlaybackQuality = this.videoReference.getVideoPlaybackQuality();
                this._checkFPS(
                    videoPlaybackQuality.droppedVideoFrames,
                    videoPlaybackQuality.totalVideoFrames
                );
            } else {
                // HTMLVideoElement doesn't include the webkit types
                this._checkFPS(
                    (this.videoReference as any).webkitDroppedFrameCount as number,
                    (this.videoReference as any).webkitDecodedFrameCount as number
                );
            }
        }
    }

    private _checkFPS(droppedFrames: number, decodedFrames:number) {
        // get dropped and decoded frames from the last period
        const currentDropped = droppedFrames - this._lastPeriodDroppedFrames;
        const currentDecoded = decodedFrames - this._lastPeriodDecodedFrames;

        this._checkDroppedFPS(currentDropped, currentDecoded);

        this._lastPeriodDroppedFrames = droppedFrames;
        this._lastPeriodDecodedFrames = decodedFrames;
    }
}
